import { apiClient } from '../lib/apiClient';
import type { TestMapping, TestMappingInput, TestMappingTarget } from './testMapping';

/**
 * Focustronic Mastertronic of an aquarium
 * (backend: src/routes/focustronic.ts, /api/v1/aquariums/:aquariumId/integrations/focustronic).
 * ReefGuide logs in to the Focustronic cloud with the owner's account. The
 * owner maps every MTE test to a ReefGuide parameter (or "do not import"),
 * then chooses the Mastertronic; its results are imported every hour.
 */

/** Outcome of the latest import, as stored by the backend. */
export interface FocustronicSyncSummary {
  at: string;
  status: 'ok' | 'failed';
  imported: number;
  updated: number;
  removed: number;
  /** Results of tests set to "do not import". */
  skipped: number;
  /** Results of tests whose mapping is not confirmed yet (absent in imports from before the mapping). */
  unmapped?: number;
  code?: string;
}

/** Never contains the password. */
export interface FocustronicConnection {
  connected: boolean;
  email?: string;
  deviceId?: string | null;
  deviceName?: string | null;
  lastSync?: FocustronicSyncSummary | null;
}

export interface FocustronicDevice {
  id: string;
  name: string;
  serialNumber: string | null;
  lastOnline: string | null;
}

export const focustronicConnectionKey = (aquariumId: string) => ['focustronic', aquariumId, 'connection'] as const;
export const focustronicDevicesKey = (aquariumId: string) => ['focustronic', aquariumId, 'devices'] as const;
export const focustronicPreviewKey = (aquariumId: string, deviceId: string) => ['focustronic', aquariumId, 'preview', deviceId] as const;
export const focustronicMappingsKey = (aquariumId: string) => ['focustronic', aquariumId, 'mappings'] as const;
export const focustronicTargetsKey = (aquariumId: string) => ['focustronic', aquariumId, 'targets'] as const;

export type FocustronicMappingInput = TestMappingInput;

export interface FocustronicImportResult {
  connection: FocustronicConnection;
  mappings: TestMapping[];
  sync: FocustronicSyncSummary;
}

function base(aquariumId: string): string {
  return `/api/v1/aquariums/${encodeURIComponent(aquariumId)}/integrations/focustronic`;
}

export async function getFocustronicConnection(aquariumId: string): Promise<FocustronicConnection> {
  const res = await apiClient.get<{ data: FocustronicConnection }>(base(aquariumId));
  return res.data;
}

/** Checks the account with Focustronic, then saves it. The password is never kept by this module. */
export async function connectFocustronic(
  aquariumId: string,
  email: string,
  password: string,
): Promise<{ connection: FocustronicConnection; devices: FocustronicDevice[] }> {
  const res = await apiClient.put<{ data: { connection: FocustronicConnection; devices: FocustronicDevice[] } }>(
    base(aquariumId),
    { email: email.trim(), password },
  );
  return res.data;
}

export async function listFocustronicDevices(aquariumId: string): Promise<FocustronicDevice[]> {
  const res = await apiClient.get<{ data: FocustronicDevice[] }>(`${base(aquariumId)}/devices`);
  return res.data;
}

/** Every test of a Mastertronic's recent results with a suggested mapping, to confirm first. Imports nothing. */
export async function previewFocustronicDevice(aquariumId: string, deviceId: string): Promise<TestMapping[]> {
  const res = await apiClient.get<{ data: TestMapping[] }>(`${base(aquariumId)}/devices/${encodeURIComponent(deviceId)}/preview`);
  return res.data;
}

/** The ReefGuide parameters a test can be mapped to, incl. the ones created for this aquarium. */
export async function listFocustronicTargets(aquariumId: string): Promise<TestMappingTarget[]> {
  const res = await apiClient.get<{ data: TestMappingTarget[] }>(`${base(aquariumId)}/targets`);
  return res.data;
}

/** Saves the confirmed mapping, chooses the Mastertronic and imports its results right away. */
export async function chooseFocustronicDevice(
  aquariumId: string,
  deviceId: string,
  mappings: FocustronicMappingInput[],
): Promise<FocustronicImportResult> {
  const res = await apiClient.put<{ data: FocustronicImportResult }>(`${base(aquariumId)}/device`, { deviceId, mappings });
  return res.data;
}

/** The stored mapping of the chosen Mastertronic (new tests flagged 'new'). */
export async function getFocustronicMappings(aquariumId: string): Promise<TestMapping[]> {
  const res = await apiClient.get<{ data: TestMapping[] }>(`${base(aquariumId)}/mappings`);
  return res.data;
}

/** Saves the mapping (imported results follow a change) and imports. */
export async function saveFocustronicMappings(aquariumId: string, mappings: FocustronicMappingInput[]): Promise<FocustronicImportResult> {
  const res = await apiClient.put<{ data: FocustronicImportResult }>(`${base(aquariumId)}/mappings`, { mappings });
  return res.data;
}

export async function syncFocustronic(
  aquariumId: string,
): Promise<{ connection: FocustronicConnection; sync: FocustronicSyncSummary }> {
  const res = await apiClient.post<{ data: { connection: FocustronicConnection; sync: FocustronicSyncSummary } }>(
    `${base(aquariumId)}/sync`,
    {},
  );
  return res.data;
}

/** Removes the account; imported measurements stay. */
export async function disconnectFocustronic(aquariumId: string): Promise<void> {
  await apiClient.delete<void>(base(aquariumId));
}
